import { useState } from "react";
import {
  Button,
  Card,
  Drawer,
  H4,
  Icon,
  IconButton,
  Title,
  TextArea,
  Tag,
  useTheme,
  useToast,
  Layout
} from "@introist/react-foundation/v2";
import styled from "styled-components";
import { api } from "services/rpc/RpcProvider";
import { useSupportContext } from "./useSupportContext";
import { ResourcePicker } from "./ResourcePicker";
import { AttachedResource, RESOURCE_TYPES, resourceKey, SupportResource } from "./resources";
import { useResourceLabel } from "./useResourceLabel";

const MAX_MESSAGE_LENGTH = 10_000;

type SupportDrawerProps = {
  open: boolean;
  onClose: () => void;
};

const StyledSupportDrawer = styled(Drawer)`
  box-sizing: border-box;
  padding: var(--spacing-xxLarge) var(--spacing-xLarge);
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid var(--palette-border-subdued);
  margin: var(--spacing-medium) 0;
`;

const SubduedTitle = styled(Title)`
  color: var(--palette-foreground-subdued);
`;

type ResourceChipProps = {
  resource: SupportResource;
  label?: string;
  onRemove: () => void;
};

const ResourceChip = ({ resource, label, onRemove }: ResourceChipProps) => {
  const lookedUp = useResourceLabel(label ? undefined : resource);
  return (
    <Tag startIcon={RESOURCE_TYPES[resource.type].icon} onRemove={onRemove}>
      {label ?? lookedUp}
    </Tag>
  );
};

const SupportForm = () => {
  const toast = useToast();
  const { capture, autoResource } = useSupportContext();
  const send = api.support.send.useMutation();

  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  // No label: the chip looks the name up itself
  const initialResources = (): AttachedResource[] =>
    autoResource ? [{ resource: autoResource }] : [];
  const [resources, setResources] = useState<AttachedResource[]>(initialResources);

  const attach = (picked: AttachedResource) => {
    const key = resourceKey(picked.resource);
    setResources(current =>
      current.some(r => resourceKey(r.resource) === key) ? current : [...current, picked]
    );
  };

  const detach = (key: string) =>
    setResources(current => current.filter(r => resourceKey(r.resource) !== key));

  const onSend = async () => {
    try {
      await send.mutateAsync({
        message,
        context: capture(),
        resources: resources.map(r => r.resource)
      });
      setMessage("");
      setResources(initialResources());
      setSent(true);
    } catch {
      toast.error("Sending failed, please try again");
    }
  };

  if (sent) {
    return (
      <Layout.Group vertical>
        <Title>Thanks — your message has been sent to Introist support.</Title>
        <Layout.Group justifyContent="flex-end">
          <Button variant="outlined" onClick={() => setSent(false)}>
            Send another
          </Button>
        </Layout.Group>
      </Layout.Group>
    );
  }

  return (
    <Layout.Group vertical>
      <Title variant="bold">How can we help?</Title>
      <TextArea
        autoFocus
        rows={8}
        placeholder="Describe your issue or question…"
        value={message}
        maxLength={MAX_MESSAGE_LENGTH}
        onChange={setMessage}
      />
      <Layout.Group vertical gap="xSmall">
        <SubduedTitle>
          Attach what your question is about. Only ids are sent, no names.
        </SubduedTitle>
        {resources.length > 0 && (
          <Layout.Group gap="xSmall" style={{ flexWrap: "wrap" }}>
            {resources.map(({ resource, label }) => {
              const key = resourceKey(resource);
              return (
                <ResourceChip
                  key={key}
                  resource={resource}
                  label={label}
                  onRemove={() => detach(key)}
                />
              );
            })}
          </Layout.Group>
        )}
        <ResourcePicker onPick={attach} />
      </Layout.Group>
      <Layout.Group justifyContent="flex-end">
        <Button
          onClick={onSend}
          loading={send.isLoading}
          disabled={!message.trim() || send.isLoading}
        >
          Send
        </Button>
      </Layout.Group>
    </Layout.Group>
  );
};

export const SupportDrawer = ({ open, onClose, ...rest }: SupportDrawerProps) => {
  const { theme } = useTheme();

  return (
    <StyledSupportDrawer {...rest} open={open} onClose={onClose} width="540px">
      <Layout.Group vertical>
        <Layout.Group justifyContent="space-between">
          <H4>Support</H4>
          <IconButton variant="blended" icon="crossLarge" onClick={onClose} />
        </Layout.Group>

        {/* Mounted only while open so closing resets the form and autoFocus fires on every open */}
        {open && <SupportForm />}

        <Divider />

        <Card hoverable onClick={() => window.open("https://docs.introist.com/")}>
          <Layout.Group justifyContent="space-between">
            <Layout.Group>
              <Icon name="bulb" color={theme.palette.primary.default} />
              <Title>Introist Help Center</Title>
            </Layout.Group>
            <Icon name="link" color={theme.palette.foreground.subdued} />
          </Layout.Group>
        </Card>
      </Layout.Group>
    </StyledSupportDrawer>
  );
};
